import React from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { LayoutGrid, CalendarRange } from "lucide-react";
import { Hero } from "@/components/Hero";
import { Dashboard } from "@/components/Dashboard";
import { Filters } from "@/components/Filters";
import { CourseCard } from "@/components/CourseCard";
import { CourseModal } from "@/components/CourseModal";
import { CalendarView } from "@/components/CalendarView";
import { CATEGORIES } from "@/lib/categories";
import { useSite } from "@/context/SiteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "formazione@rischio-zero.it";
const DEFAULT_FILTERS = { search: "", month: "all", category: "all", duration: "all", location: "all", type: "all" };

const durationMatch = (hours, bucket) => {
  if (bucket === "all") return true;
  if (bucket === "short") return hours <= 4;
  if (bucket === "mid") return hours >= 5 && hours <= 8;
  if (bucket === "long") return hours >= 9 && hours <= 16;
  if (bucket === "xl") return hours > 16;
  return true;
};

export default function Home() {
  const [courses, setCourses] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [filters, setFilters] = React.useState(DEFAULT_FILTERS);
  const [view, setView] = React.useState("grid");
  const [selected, setSelected] = React.useState(null);
  const { cats, catMap, settings: s } = useSite();

  React.useEffect(() => {
    axios.get(`${API}/courses`).then((r) => setCourses(r.data.courses)).catch(console.error);
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(console.error);
  }, []);

  const locations = React.useMemo(() => [...new Set(courses.map((c) => c.location))], [courses]);

  const filtered = React.useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return courses.filter((c) => {
      if (q && !(`${c.name} ${catMap[c.category]?.label || ""} ${c.description}`.toLowerCase().includes(q))) return false;
      if (filters.category !== "all" && c.category !== filters.category) return false;
      if (filters.type !== "all" && c.type !== filters.type) return false;
      if (filters.location !== "all" && c.location !== filters.location) return false;
      if (!durationMatch(c.duration_hours, filters.duration)) return false;
      if (filters.month !== "all") {
        const m = Number(filters.month);
        if (!c.dates.some((d) => Number(d.split("-")[1]) === m)) return false;
      }
      return true;
    });
  }, [courses, filters]);

  const bookGeneric = () => { window.location.href = `mailto:${EMAIL}?subject=${encodeURIComponent("Richiesta prenotazione corso")}`; };

  const categoryList = cats;

  return (
    <>
      <Hero onBook={bookGeneric} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <section className="-mt-2"><Dashboard stats={stats} /></section>

        <section id="categorie" className="mt-16 sm:mt-24">
          <SectionHeader eyebrow={s.cat_eyebrow || "Categorie"} title={s.cat_title || "Trova il corso giusto per te"} subtitle={s.cat_subtitle || "Filtra i corsi per categoria. Ogni ambito ha un colore dedicato."} />
          <div className="mt-8 flex flex-wrap gap-2.5" data-testid="category-badges">
            {categoryList.map((cat, i) => {
              const key = cat.key || cat.id;
              const active = filters.category === key;
              return (
                <motion.button key={key} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: i * 0.04 }}
                  data-testid={`category-badge-${key}`}
                  onClick={() => { setFilters((f) => ({ ...f, category: active ? "all" : key })); setView("grid"); document.getElementById("corsi")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-600 border transition-all hover:scale-[1.03]"
                  style={active ? { background: cat.color, color: "#fff", borderColor: cat.color } : { background: `${cat.color}0F`, color: cat.color, borderColor: "transparent" }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: active ? "#fff" : cat.color }} />
                  {cat.label}<span className={`text-xs ${active ? "opacity-80" : "opacity-60"}`}>{cat.count}</span>
                </motion.button>
              );
            })}
          </div>
        </section>

        <section id="corsi" className="mt-16 sm:mt-24 scroll-mt-24">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <SectionHeader eyebrow={s.courses_eyebrow || "Calendario 2026"} title={s.courses_title || "Tutti i corsi in programma"} subtitle={s.courses_subtitle || "Cerca, filtra e apri ogni corso per vedere i dettagli completi."} noMargin />
            <div className="inline-flex items-center bg-gray-100 rounded-full p-1 self-start" data-testid="view-toggle">
              <ToggleBtn active={view === "grid"} onClick={() => setView("grid")} icon={LayoutGrid} label="Griglia" testid="view-grid" />
              <ToggleBtn active={view === "calendar"} onClick={() => setView("calendar")} icon={CalendarRange} label="Calendario" testid="view-calendar" />
            </div>
          </div>
          <div className="mt-8"><Filters filters={filters} setFilters={setFilters} locations={locations} resultsCount={filtered.length} /></div>
          <div id="calendario" className="mt-10 scroll-mt-24">
            {view === "grid" ? (
              filtered.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-testid="course-grid">
                  {filtered.map((c, i) => <CourseCard key={c.id} course={c} index={i} onOpen={setSelected} />)}
                </div>
              ) : (
                <div data-testid="empty-state" className="text-center py-20 bg-gray-50 rounded-3xl border border-gray-100">
                  <p className="font-display font-700 text-xl text-[#EA6A0A]">Nessun corso trovato</p>
                  <p className="text-gray-500 mt-2 text-sm">Prova a modificare i filtri o la ricerca.</p>
                  <button data-testid="empty-reset" onClick={() => setFilters(DEFAULT_FILTERS)} className="mt-5 px-5 py-2.5 rounded-full bg-[#EA6A0A] text-white text-sm font-600 hover:bg-[#002244] transition-colors">Azzera filtri</button>
                </div>
              )
            ) : <CalendarView courses={filtered} onOpen={setSelected} />}
          </div>
        </section>
      </main>
      <CourseModal course={selected} onClose={() => setSelected(null)} />
    </>
  );
}

export const SectionHeader = ({ eyebrow, title, subtitle, noMargin, accent = "#EA6A0A", titleColor = "#EA6A0A" }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5 }} className={noMargin ? "" : ""}>
    <span className="inline-block text-xs font-700 uppercase tracking-widest mb-3" style={{ color: accent }}>{eyebrow}</span>
    <h2 className="font-display font-800 text-3xl sm:text-4xl tracking-tight leading-tight" style={{ color: titleColor }}>{title}</h2>
    {subtitle && <p className="mt-3 text-gray-500 text-base max-w-xl">{subtitle}</p>}
  </motion.div>
);

const ToggleBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-600 transition-all ${active ? "bg-white text-[#EA6A0A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
    <Icon size={16} />{label}
  </button>
);
